// frontend/src/context/CartContext.js
import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { cartApi } from '../api/cart';
import { useAuth } from './AuthContext';

const CartContext = createContext();
export const useCart = () => useContext(CartContext);

const POLL_INTERVAL = 5000; // Real-time sync every 5 seconds

const sanitizeCartItem = (item) => ({
    ...item,
    price: parseFloat(item.price) || 0,
    quantity: parseInt(item.quantity) || 0,
});

const getCartItemImage = (product) => {
    const explicitUrl = product.images?.front || product.images?.front_jpg || product.itemurl;
    if (explicitUrl) return explicitUrl;
    if (product.images && typeof product.images === 'object') {
        const firstImage = Object.values(product.images).find(url => url);
        if (firstImage) return firstImage;
    }
    if (product.image) return product.image;
    return 'https://via.placeholder.com/100';
};

const getCartItemPrice = (product) => {
    const price = product.price || product.current_price || product.list_price;
    return parseFloat(price) || 0;
};

export const CartProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef(null);
  const lastServerStateRef = useRef('[]');

  // =============================================
  // FETCH FROM SERVER
  // =============================================
  const fetchServerCart = useCallback(async (silent = false) => {
    if (!isAuthenticated) return;
    if (!silent) setLoading(true);
    try {
      const data = await cartApi.getCart();
      const sanitizedItems = (data.items || []).map(sanitizeCartItem);
      const newState = JSON.stringify(sanitizedItems);

      // Only update state if server data actually changed — prevents flicker
      if (newState !== lastServerStateRef.current) {
        lastServerStateRef.current = newState;
        setCartItems(sanitizedItems);
      }
    } catch (err) {
      console.error("Failed to fetch cart:", err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [isAuthenticated]);

  // =============================================
  // REAL-TIME POLLING
  // =============================================
  useEffect(() => {
    if (isAuthenticated) {
      fetchServerCart();

      // Poll every 5 seconds silently for real-time updates
      pollRef.current = setInterval(() => {
        fetchServerCart(true);
      }, POLL_INTERVAL);
    } else {
      // Guest: use localStorage
      const stored = localStorage.getItem('cartItems');
      if (stored) {
        try {
          setCartItems(JSON.parse(stored).map(sanitizeCartItem));
        } catch (e) {
          localStorage.removeItem('cartItems');
          setCartItems([]);
        }
      }
      clearInterval(pollRef.current);
    }

    return () => clearInterval(pollRef.current);
  }, [isAuthenticated, fetchServerCart]);

  // =============================================
  // ADD TO CART
  // =============================================
  const addToCart = async (product, quantity = 1) => {
    if (!product || !product.id) return;

    const itemType = product.type || (product.year ? 'vehicle' : 'part');
    const newItem = sanitizeCartItem({
      id: product.id,
      itemname: product.itemname || `${product.year} ${product.make} ${product.model}`,
      price: getCartItemPrice(product),
      image: getCartItemImage(product),
      type: itemType,
      quantity: quantity
    });

    // Optimistic update
    setCartItems(prev => {
      const existing = prev.find(i => i.id === newItem.id);
      if (existing) {
        return prev.map(i => i.id === newItem.id ? { ...i, quantity: i.quantity + newItem.quantity } : i);
      }
      return [...prev, newItem];
    });

    if (isAuthenticated) {
      try {
        await cartApi.addToCart(product.id, itemType, quantity);
        fetchServerCart(true);
      } catch (err) {
        console.error("Failed to sync add to cart:", err);
        fetchServerCart();
      }
    } else {
      setCartItems(prev => {
        const updated = prev.find(i => i.id === newItem.id)
          ? prev.map(i => i.id === newItem.id ? { ...i, quantity: i.quantity + newItem.quantity } : i)
          : [...prev, newItem];
        localStorage.setItem('cartItems', JSON.stringify(updated));
        return updated;
      });
    }
  };

  // =============================================
  // UPDATE QUANTITY (with server sync)
  // =============================================
  const updateQuantity = async (id, qty) => {
    const newQty = parseInt(qty) || 0;
    if (newQty < 0) return;

    if (newQty === 0) {
      return removeFromCart(id);
    }

    // Optimistic update
    setCartItems(prev => {
      const updated = prev.map(item => item.id === id ? { ...item, quantity: newQty } : item);
      if (!isAuthenticated) localStorage.setItem('cartItems', JSON.stringify(updated));
      return updated;
    });

    if (isAuthenticated) {
      try {
        await cartApi.updateItem(id, newQty);
        fetchServerCart(true);
      } catch (err) {
        console.error("Failed to sync quantity update:", err);
        fetchServerCart();
      }
    }
  };

  // =============================================
  // REMOVE ITEM (with server sync)
  // =============================================
  const removeFromCart = async (productId) => {
    // Optimistic update
    setCartItems(prev => {
      const updated = prev.filter(i => i.id !== productId);
      if (!isAuthenticated) localStorage.setItem('cartItems', JSON.stringify(updated));
      return updated;
    });

    if (isAuthenticated) {
      try {
        await cartApi.removeItem(productId);
        fetchServerCart(true);
      } catch (err) {
        console.error("Failed to sync remove from cart:", err);
        fetchServerCart();
      }
    }
  };

  // =============================================
  // CLEAR CART
  // =============================================
  const clearCart = async () => {
    setCartItems([]);
    localStorage.removeItem('cartItems');
    if (isAuthenticated) {
      try {
        await cartApi.clearCart();
      } catch (err) {
        console.error("Failed to clear cart:", err);
      }
    }
  };

  const cartCount = cartItems.reduce((total, item) => total + (item.quantity || 0), 0);
  const cartTotal = cartItems.reduce((total, item) => total + ((item.price || 0) * (item.quantity || 0)), 0);

  return (
    <CartContext.Provider value={{
      cartItems,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      refreshCart: fetchServerCart,
      cartCount,
      cartTotal,
      loading
    }}>
      {children}
    </CartContext.Provider>
  );
};